// =============================================================================
// Origin UI — src/components/Sidebar.tsx
// Role-aware sidebar navigation with collapsible sections.
// Order: People → Recruit → Time Off → Performance → Compliance → Credit Cards → Requests → Approvals → IT Ops → Reporting → Admin
// Updated: B.24 — module flags gating for Performance and Requests sections.
// Updated: CreditCardAdmin role — CC Admin link visible to HRAdmin OR CreditCardAdmin.
// Updated: CM-6 — Compliance section (all staff) + Dashboard link (Compliance role only).
// Updated: Cardholder gating — CC section only visible to cardholders, CC admins, HR admins.
// =============================================================================

import { useState } from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { useModuleFlags } from '@/hooks/useModuleFlags';
import styles from './Sidebar.module.css';

function NavSection({ label, children, defaultOpen, pathPrefixes }: {
  label: string; children: React.ReactNode; defaultOpen?: boolean; pathPrefixes?: string[];
}) {
  const location = useLocation();
  const isActiveSection = pathPrefixes?.some(p => location.pathname.startsWith(p)) ?? false;
  const [open, setOpen] = useState(defaultOpen || isActiveSection);

  return (
    <div className={styles.navSection}>
      <button
        className={`${styles.navSectionLabel} ${open ? styles.navSectionLabelOpen : ''}`}
        onClick={() => setOpen(!open)}
        type="button"
      >
        <span>{label}</span>
        <span className={`${styles.chevron} ${open ? styles.chevronOpen : ''}`}>›</span>
      </button>
      {open && <div className={styles.navSectionItems}>{children}</div>}
    </div>
  );
}

export function Sidebar() {
  const { userName, userEmail, roles, logout } = useAuth();
  const { isModuleEnabled } = useModuleFlags();
  const navigate = useNavigate();
  const location = useLocation();

  const initials = userName
    .split(' ')
    .map((part) => part[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);

  // My Profile redirects /profile → /employees/{id}, so we need to detect
  // when we're viewing our own employee record to highlight "My Profile"
  // instead of "Employees". We track this via sessionStorage set by MyProfileRedirect.
  const isMyProfileActive = location.pathname === '/profile'
    || sessionStorage.getItem('origin_my_profile_active') === location.pathname;

  // The Employees link should NOT be active when viewing own profile
  const isEmployeesActive = (navIsActive: boolean) =>
    navIsActive && !isMyProfileActive;

  // Credit Cards section visible to cardholders, CC admins, and HR admins
  const showCreditCards = isModuleEnabled('creditCards')
    && (roles.isCardholder || roles.isCreditCardAdmin || roles.isHRAdmin);

  return (
    <aside className={styles.sidebar}>
      {/* ─── Logo / Brand ─── */}
      <div className={styles.brand} onClick={() => navigate('/')} style={{ cursor: 'pointer' }}>
        <div className={styles.logoMark}>
          <svg width="32" height="32" viewBox="0 0 52 52" xmlns="http://www.w3.org/2000/svg">
            <path d="M4 44 C8 32 16 20 26 14 C34 9 42 10 48 18" fill="none" stroke="#00B0B9" strokeWidth="5.5" strokeLinecap="round"/>
            <path d="M8 6 C12 18 20 30 30 36 C38 41 46 40 50 32" fill="none" stroke="#FF8300" strokeWidth="5.5" strokeLinecap="round"/>
            <circle cx="26" cy="25" r="4.5" fill="#FFFFFF"/>
          </svg>
        </div>
        <div>
          <div className={styles.brandName}>Origin</div>
          <div className={styles.brandSuite}>Orchestra Suite</div>
        </div>
      </div>

      {/* ─── Navigation ─── */}
      <nav className={styles.nav}>
        {/* Home */}
        <div className={styles.navSection}>
          <NavLink to="/" end className={({ isActive }) => `${styles.navLink} ${isActive ? styles.navLinkActive : ''}`}>
            <span className={styles.navIcon}>🏠</span> Home
          </NavLink>
        </div>

        {/* People — HR Admin and OQTMan see Employees; everyone sees My Profile + Team View */}
        {(roles.isHRAdmin || roles.isOQTMan) ? (
          <NavSection label="People" pathPrefixes={['/employees', '/team', '/profile']}>
            <NavLink to="/profile" className={() => `${styles.navLink} ${isMyProfileActive ? styles.navLinkActive : ''}`}>
              <span className={styles.navIcon}>👤</span> My Profile
            </NavLink>
            <NavLink to="/employees" end onClick={() => sessionStorage.removeItem('origin_my_profile_active')} className={({ isActive }) => `${styles.navLink} ${isEmployeesActive(isActive) ? styles.navLinkActive : ''}`}>
              <span className={styles.navIcon}>👥</span> Employees
            </NavLink>
            <NavLink to="/team" className={({ isActive }) => `${styles.navLink} ${isActive ? styles.navLinkActive : ''}`}>
              <span className={styles.navIcon}>🏢</span> Team View
            </NavLink>
          </NavSection>
        ) : (
          <NavSection label="People" pathPrefixes={['/profile', '/team', '/employees']}>
            <NavLink to="/profile" className={() => `${styles.navLink} ${isMyProfileActive ? styles.navLinkActive : ''}`}>
              <span className={styles.navIcon}>👤</span> My Profile
            </NavLink>
            <NavLink to="/team" className={({ isActive }) => `${styles.navLink} ${isActive ? styles.navLinkActive : ''}`}>
              <span className={styles.navIcon}>🏢</span> Team View
            </NavLink>
          </NavSection>
        )}

        {/* Recruit — HR Admin only, gated on recruit module flag */}
        {roles.isHRAdmin && isModuleEnabled('recruit') && (
          <NavSection label="Recruit" pathPrefixes={['/offers']}>
            <NavLink to="/offers" className={({ isActive }) => `${styles.navLink} ${isActive ? styles.navLinkActive : ''}`}>
              <span className={styles.navIcon}>📨</span> Offers
            </NavLink>
          </NavSection>
        )}

        {/* Time Off */}
        <NavSection label="Time Off" pathPrefixes={['/leave']}>
          <NavLink to="/leave" end className={({ isActive }) => `${styles.navLink} ${isActive ? styles.navLinkActive : ''}`}>
            <span className={styles.navIcon}>🏖️</span> My Leave
          </NavLink>
          <NavLink to="/leave/team" className={({ isActive }) => `${styles.navLink} ${isActive ? styles.navLinkActive : ''}`}>
            <span className={styles.navIcon}>📅</span> Team Calendar
          </NavLink>
          {(roles.isHRAdmin || roles.isManager) && (
            <NavLink to="/leave/reports" className={({ isActive }) => `${styles.navLink} ${isActive ? styles.navLinkActive : ''}`}>
              <span className={styles.navIcon}>📊</span> Leave Reports
            </NavLink>
          )}
        </NavSection>

        {/* Performance — gated on appraisals module flag */}
        {isModuleEnabled('appraisals') && (
          <NavSection label="Performance" pathPrefixes={['/evaluations']}>
            <NavLink to="/evaluations/mine" className={({ isActive }) => `${styles.navLink} ${isActive ? styles.navLinkActive : ''}`}>
              <span className={styles.navIcon}>📝</span> My Evaluation
            </NavLink>
            {(roles.isHRAdmin || roles.isManager) && (
              <NavLink to="/evaluations/team" className={({ isActive }) => `${styles.navLink} ${isActive ? styles.navLinkActive : ''}`}>
                <span className={styles.navIcon}>👥</span> Team Evaluations
              </NavLink>
            )}
            {roles.isHRAdmin && (
              <NavLink to="/evaluations/reports" className={({ isActive }) => `${styles.navLink} ${isActive ? styles.navLinkActive : ''}`}>
                <span className={styles.navIcon}>📊</span> Evaluation Reports
              </NavLink>
            )}
          </NavSection>
        )}

        {/* Compliance — gated on compliance module flag; landing for all staff, Dashboard for Compliance role only */}
        {isModuleEnabled('Compliance') && (
          <NavSection label="Compliance" pathPrefixes={['/compliance']}>
            <NavLink to="/compliance" end className={({ isActive }) => `${styles.navLink} ${isActive ? styles.navLinkActive : ''}`}>
              <span className={styles.navIcon}>🛡️</span> Overview
            </NavLink>
            {roles.isComplianceExec && (
              <NavLink to="/compliance/dashboard" className={({ isActive }) => `${styles.navLink} ${isActive ? styles.navLinkActive : ''}`}>
                <span className={styles.navIcon}>📊</span> Dashboard
              </NavLink>
            )}
          </NavSection>
        )}

        {/* Credit Cards — only visible to cardholders, CC admins, HR admins */}
        {showCreditCards && (
          <NavSection label="Credit Cards" pathPrefixes={['/credit-cards']}>
            <NavLink to="/credit-cards/my-card" className={({ isActive }) => `${styles.navLink} ${isActive ? styles.navLinkActive : ''}`}>
              <span className={styles.navIcon}>💳</span> My Card
            </NavLink>
            <NavLink to="/credit-cards/approvals" className={({ isActive }) => `${styles.navLink} ${isActive ? styles.navLinkActive : ''}`}>
              <span className={styles.navIcon}>✅</span> Approvals
            </NavLink>
            {(roles.isHRAdmin || roles.isCreditCardAdmin) && (<>
              <NavLink to="/credit-cards/admin" className={({ isActive }) => `${styles.navLink} ${isActive ? styles.navLinkActive : ''}`}>
                <span className={styles.navIcon}>⚙️</span> Admin
              </NavLink>
              <NavLink to="/credit-cards/reports" className={({ isActive }) => `${styles.navLink} ${isActive ? styles.navLinkActive : ""}`}>
                <span className={styles.navIcon}>📊</span> Reports
              </NavLink>
            </>)}
          </NavSection>
        )}

        {/* Requests — gated on onboarding module flag */}
        {isModuleEnabled('onboarding') && (
          <NavSection label="Requests" pathPrefixes={['/requests']}>
            <NavLink to="/requests" end className={({ isActive }) => `${styles.navLink} ${isActive ? styles.navLinkActive : ''}`}>
              <span className={styles.navIcon}>📋</span> My Requests
            </NavLink>
            {(roles.isHRAdmin || roles.isManager) && (
              <NavLink to="/requests/new" className={({ isActive }) => `${styles.navLink} ${isActive ? styles.navLinkActive : ''}`}>
                <span className={styles.navIcon}>➕</span> Create Request
              </NavLink>
            )}
          </NavSection>
        )}

        {/* Approvals */}
        <NavSection label="Approvals" pathPrefixes={['/approvals']}>
          <NavLink to="/approvals" className={({ isActive }) => `${styles.navLink} ${isActive ? styles.navLinkActive : ''}`}>
            <span className={styles.navIcon}>✅</span> Approvals Inbox
          </NavLink>
        </NavSection>

        {/* IT Operations */}
        {(roles.isITOps || roles.isHRAdmin) && (
          <NavSection label="IT Operations" pathPrefixes={['/tasks', '/it-assets']}>
            <NavLink to="/tasks" className={({ isActive }) => `${styles.navLink} ${isActive ? styles.navLinkActive : ''}`}>
              <span className={styles.navIcon}>⚙️</span> Tasks Queue
            </NavLink>
            <NavLink to="/it-assets" end className={({ isActive }) => `${styles.navLink} ${isActive ? styles.navLinkActive : ''}`}>
              <span className={styles.navIcon}>💻</span> IT Assets
            </NavLink>
            <NavLink to="/it-assets/reports" className={({ isActive }) => `${styles.navLink} ${isActive ? styles.navLinkActive : ''}`}>
              <span className={styles.navIcon}>📊</span> Asset Reports
            </NavLink>
          </NavSection>
        )}

        {/* Reporting */}
        {(roles.isHRAdmin || roles.isManager) && (
          <NavSection label="Reporting" pathPrefixes={['/reports']}>
            <NavLink to="/reports" end className={({ isActive }) => `${styles.navLink} ${isActive ? styles.navLinkActive : ''}`}>
              <span className={styles.navIcon}>📊</span> Reports & Audit
            </NavLink>
            <NavLink to="/reports/document-expiry" className={({ isActive }) => `${styles.navLink} ${isActive ? styles.navLinkActive : ''}`}>
              <span className={styles.navIcon}>📋</span> Document Expiry
            </NavLink>
          </NavSection>
        )}

        {/* Administration */}
        {(roles.isHRAdmin || roles.isSysAdmin || roles.isITOps) && (
          <NavSection label="Administration" pathPrefixes={['/admin']}>
            <NavLink to="/admin" className={({ isActive }) => `${styles.navLink} ${isActive ? styles.navLinkActive : ''}`}>
              <span className={styles.navIcon}>🔧</span> Configuration
            </NavLink>
          </NavSection>
        )}
      </nav>

      {/* ─── User Profile / Logout ─── */}
      <div className={styles.userSection}>
        <div className={styles.userAvatar}>{initials}</div>
        <div className={styles.userInfo}>
          <div className={styles.userName}>{userName}</div>
          <div className={styles.userEmail}>{userEmail}</div>
        </div>
        <button className={styles.logoutBtn} onClick={logout} title="Sign out">↗</button>
      </div>
    </aside>
  );
}
